import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth, API } from "../App";
import { 
  Plus, History, Crown, LogOut, Clock, 
  CheckCircle2, ArrowRight, BarChart3, Zap, Heart, BookOpen
} from "lucide-react";
import { StatsGrid, DecisionItem } from "../components/DashboardComponents";
import FuturaIcon from "../components/FuturaIcon";
import { useLanguage } from "../i18n/LanguageContext";
import { LanguageSelector } from "../components/LanguageSelector";
import { useIsWebView } from "../hooks/useIsWebView";
import axios from "axios";

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const isWebView = useIsWebView();
  const [decisions, setDecisions] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [decisionsRes, subRes] = await Promise.all([
          axios.get(`${API}/decisions`, { withCredentials: true }),
          axios.get(`${API}/subscription/status`, { withCredentials: true })
        ]);
        setDecisions(decisionsRes.data);
        setSubscription(subRes.data);
      } catch (error) {
        console.error("Dashboard fetch failed:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once on mount, API is a module constant
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const canSimulate = subscription?.can_simulate ?? true;

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E5EA] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#002FA7] to-[#5B21B6] flex items-center justify-center p-1.5">
              <FuturaIcon className="w-full h-full text-white" />
            </div>
            <span className="font-['Outfit'] font-semibold text-xl tracking-tight text-[#111111]">FUTURA</span>
          </Link>
          
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/history")}
              className="text-[#6B7280] hover:text-[#111111]"
              data-testid="dashboard-history-btn"
            >
              <History className="w-5 h-5 mr-2" />
              {t("dash_history")}
            </Button>
            {!isWebView && (
              <Button
                variant="ghost"
                onClick={() => navigate("/pricing")}
                className="text-[#6B7280] hover:text-[#111111]"
                data-testid="dashboard-pricing-btn"
              >
                <Crown className="w-5 h-5 mr-2" />
                {t("dash_plans")}
              </Button>
            )}
            {(user?.plan === "decide" || user?.plan === "avanza") && (
              <Button
                variant="ghost"
                onClick={() => navigate("/bienestar")}
                className="text-[#6B7280] hover:text-[#111111]"
                data-testid="dashboard-bienestar-btn"
              >
                <Heart className="w-5 h-5 mr-2" />
                {t("dash_wellbeing")}
              </Button>
            )}
            {user?.plan === "avanza" && (
              <Button
                variant="ghost"
                onClick={() => navigate("/biblioteca")}
                className="text-[#6B7280] hover:text-[#111111]"
                data-testid="dashboard-biblioteca-btn"
              >
                <BookOpen className="w-5 h-5 mr-2" />
                {t("dash_library")}
              </Button>
            )}
            <div className="h-8 w-px bg-[#E5E5EA]"></div>
            <LanguageSelector variant="light" />
            <div
              className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => navigate("/perfil")}
              data-testid="dashboard-profile-link"
            >
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#002FA7] to-[#5B21B6] flex items-center justify-center text-white font-medium text-sm">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-medium text-[#111111]">{user?.name}</p>
                <p className="text-xs text-[#6B7280] capitalize">{subscription?.plan || "free"}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-[#6B7280] hover:text-[#EF4444]"
              data-testid="dashboard-logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="mb-8 animate-fade-in">
          <h1 className="font-['Outfit'] text-3xl font-semibold text-[#111111] tracking-tight mb-2">
            {t("dash_hello")}, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-[#6B7280]">
            {t("dash_question")}
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-3xl p-6 card-hover animate-fade-in stagger-1" data-testid="stats-simulations">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-2xl bg-[#002FA7]/10 flex items-center justify-center">
                <Zap className="w-6 h-6 text-[#002FA7]" />
              </div>
              <span className="text-xs font-medium text-[#6B7280] uppercase tracking-wide">
                {t("dash_simulations")}
              </span>
            </div>
            <p className="text-3xl font-['Outfit'] font-bold text-[#111111]">
              {subscription?.simulations_used || 0}
              <span className="text-lg text-[#6B7280] font-normal">
                /{subscription?.simulations_limit === "unlimited" ? "∞" : subscription?.simulations_limit || 1}
              </span>
            </p>
            <p className="text-sm text-[#6B7280] mt-1">
              {subscription?.reset_type === "daily" ? t("dash_used_today") : subscription?.reset_type === "none" ? t("dash_unlimited") : t("dash_used_month")}
            </p>
            {!subscription?.can_simulate && subscription?.next_reset && (
              <p className="text-xs text-[#002FA7] mt-2 font-medium">{t("dash_resets")} {new Date(subscription.next_reset).toLocaleString()}</p>
            )}
          </div>

          <div className="bg-white rounded-3xl p-6 card-hover animate-fade-in stagger-2" data-testid="stats-decisions">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-2xl bg-[#5B21B6]/10 flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-[#5B21B6]" />
              </div>
              <span className="text-xs font-medium text-[#6B7280] uppercase tracking-wide">
                {t("dash_decisions")}
              </span>
            </div>
            <p className="text-3xl font-['Outfit'] font-bold text-[#111111]">
              {decisions.length}
            </p>
            <p className="text-sm text-[#6B7280] mt-1">{t("dash_in_history")}</p>
          </div>

          <div className="bg-white rounded-3xl p-6 card-hover animate-fade-in stagger-3" data-testid="stats-plan">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-2xl bg-[#10B981]/10 flex items-center justify-center">
                <Crown className="w-6 h-6 text-[#10B981]" />
              </div>
              <span className="text-xs font-medium text-[#6B7280] uppercase tracking-wide">
                Plan
              </span>
            </div>
            <p className="text-3xl font-['Outfit'] font-bold text-[#111111] capitalize">
              {subscription?.plan_name || subscription?.plan || "Descubre"}
            </p>
            {(subscription?.plan === "descubre" || subscription?.plan === "free") && !isWebView && (
              <Button
                variant="link"
                onClick={() => navigate("/pricing")}
                className="text-[#002FA7] p-0 h-auto mt-1"
                data-testid="upgrade-link"
              >
                {t("dash_upgrade")} →
              </Button>
            )}
            {(subscription?.plan === "descubre" || subscription?.plan === "free") && isWebView && (
              <p className="text-xs text-[#6B7280] mt-1" data-testid="webview-sub-text-dashboard">{t("webview_sub_managed")}</p>
            )}
          </div>
        </div>

        {/* New Simulation CTA */}
        <div className="bg-gradient-to-br from-[#002FA7] to-[#5B21B6] rounded-3xl p-8 mb-8 animate-fade-in stagger-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="font-['Outfit'] text-2xl font-semibold text-white mb-2">
                {t("dash_new_sim")}
              </h2>
              <p className="text-white/80">
                {t("dash_new_sim_desc")}
              </p>
            </div>
            <Button
              onClick={() => navigate("/new-decision")}
              disabled={!canSimulate}
              className="bg-white hover:bg-[#F5F5F7] text-[#002FA7] font-medium rounded-full px-8 py-6 btn-lift whitespace-nowrap"
              data-testid="new-simulation-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t("dash_create_sim")}
            </Button>
          </div>
          {!canSimulate && (
            <p className="text-white/70 text-sm mt-4">
              {t("dash_limit_reached")}
            </p>
          )}
        </div>

        {/* Wellbeing & Library Quick Access */}
        {(user?.plan === "decide" || user?.plan === "avanza") && (
          <div className="grid sm:grid-cols-2 gap-4 mb-8 animate-fade-in stagger-5">
            <div
              onClick={() => navigate("/bienestar")}
              className="bg-white rounded-3xl p-6 card-hover cursor-pointer border border-transparent hover:border-[#10B981]/20 transition-all"
              data-testid="dashboard-bienestar-card"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-[#10B981]/10 flex items-center justify-center">
                  <Heart className="w-6 h-6 text-[#10B981]" />
                </div>
                <div>
                  <h3 className="font-['Outfit'] font-semibold text-[#111111]">{t("dash_emotional_tracking")}</h3>
                  <p className="text-sm text-[#6B7280]">{t("dash_emotional_desc")}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-[#6B7280] ml-auto" />
              </div>
            </div>
            {user?.plan === "avanza" && (
              <div
                onClick={() => navigate("/biblioteca")}
                className="bg-white rounded-3xl p-6 card-hover cursor-pointer border border-transparent hover:border-amber-500/20 transition-all"
                data-testid="dashboard-biblioteca-card"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-amber-500/10 flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-amber-500" />
                  </div>
                  <div>
                    <h3 className="font-['Outfit'] font-semibold text-[#111111]">{t("dash_premium_library")}</h3>
                    <p className="text-sm text-[#6B7280]">{t("dash_premium_desc")}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#6B7280] ml-auto" />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Recent Decisions */}
        <div className="animate-fade-in stagger-5">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-['Outfit'] text-xl font-semibold text-[#111111]">
              {t("dash_recent")}
            </h2>
            {decisions.length > 0 && (
              <Button
                variant="ghost"
                onClick={() => navigate("/history")}
                className="text-[#002FA7]"
                data-testid="view-all-btn"
              >
                {t("dash_view_all")}
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>

          {loading ? (
            <div className="grid gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-2xl p-6 animate-pulse">
                  <div className="h-5 bg-[#E5E5EA] rounded w-1/3 mb-3"></div>
                  <div className="h-4 bg-[#E5E5EA] rounded w-2/3"></div>
                </div>
              ))}
            </div>
          ) : decisions.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-[#F5F5F7] flex items-center justify-center mx-auto mb-4">
                <FuturaIcon className="w-8 h-8 text-[#6B7280]" />
              </div>
              <h3 className="font-['Outfit'] text-lg font-semibold text-[#111111] mb-2">
                {t("dash_no_decisions")}
              </h3>
              <p className="text-[#6B7280] mb-6">
                {t("dash_no_decisions_hint")}
              </p>
              <Button
                onClick={() => navigate("/new-decision")}
                className="bg-[#002FA7] hover:bg-[#001d6a] text-white rounded-full px-6"
                data-testid="empty-state-btn"
              >
                {t("dash_first_sim")}
              </Button>
            </div>
          ) : (
            <div className="grid gap-4">
              {decisions.slice(0, 5).map((decision, index) => (
                <div
                  key={decision.decision_id}
                  className="bg-white rounded-2xl p-6 card-hover cursor-pointer"
                  onClick={() => {
                    if (decision.status === "completed") {
                      navigate(`/result/${decision.decision_id}`);
                    } else {
                      navigate(`/simulation/${decision.decision_id}`);
                    }
                  }}
                  data-testid={`decision-card-${index}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-['Outfit'] font-semibold text-[#111111]">
                          {decision.title}
                        </h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          decision.status === "completed" 
                            ? "bg-[#10B981]/10 text-[#10B981]" 
                            : "bg-[#F59E0B]/10 text-[#F59E0B]"
                        }`}>
                          {decision.status === "completed" ? t("dash_completed") : t("dash_pending")}
                        </span>
                      </div>
                      <p className="text-[#6B7280] text-sm line-clamp-1">
                        {decision.description}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 text-[#6B7280] text-sm ml-4">
                      <Clock className="w-4 h-4" />
                      {new Date(decision.created_at).toLocaleDateString("es-ES", {
                        day: "numeric",
                        month: "short"
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
